import type { Translations } from "./index";

const de: Translations = {
  header: { title: "Drumlog", subtitle: "Dein Übungs-Companion" },
  metronome: { title: "Metronom", tapTempo: "Tap Tempo", timeSignature: "Taktart" },
  practiceSession: {
    title: "Übungs-Session", start: "Session starten", stop: "Beenden", pause: "Pause", resume: "Fortsetzen",
    thisWeek: "Diese Woche", streak: "Streak", days: "Tage", recentSessions: "Letzte Sessions",
    today: "Heute", weeklyProgress: "Wochenfortschritt", latestSession: "Letzte Session", startFirstSession: "Erste Session starten",
    freePractice: "Freies Üben", noRecentSessions: "Noch keine Sessions",
    studioToday: "Heute im Studio", readyToPractice: "Bereit für die nächste Session", sessionRunning: "Session läuft", sessionPaused: "Session pausiert",
    bestDay: "Bester Tag", avgActiveDay: "Ø aktiver Tag", weekEmptyHint: "Starte eine Session, damit dein Wochenrhythmus sichtbar wird.",
    onboardingTitle: "Willkommen im Studio", onboardingBody: "Starte deine erste Session – deine Statistiken erscheinen danach automatisch hier.",
    onboardingMidiHint: "Tipp: Verbinde dein MIDI-Kit für Hit-Tracking.",
    editSession: "Session bearbeiten", sessionTitle: "Titel", sessionNotes: "Notizen",
    sessionNotesPlaceholder: "Was hast du geübt? Fortschritte, Songs, Übungen...",
    sessionSaved: "Session gespeichert", deleteSession: "Session löschen", sessionDeleted: "Session gelöscht",
    duration: "Dauer", back: "Zurück",
    addExercise: "Übung hinzufügen", exerciseTitle: "Bezeichnung", exerciseTitlePlaceholder: "z.B. Single Stroke Roll, Back in Black...",
    exerciseType: "Typ", typeWarmup: "Aufwärmen", typeRudiment: "Rudiment", typeSong: "Song",
    sessionItems: "Geübte Einträge", noSessionItems: "Noch keine Einträge – füge hinzu was du geübt hast",
    addToSession: "Hinzufügen",
    fromLibrary: "Aus Bibliothek", createNew: "Neu erstellen", searchPlaceholder: "Suchen...", newItemCreated: "Neuer Eintrag erstellt",
    convertHint: "Wandle diese freie Session in eine strukturierte Übung um – füge unten Übungen, Songs oder Warm-ups hinzu!",
  },
  premium: { title: "Vollständige Features", description: "Nimm deine Übungs-Sessions auf, höre sie dir an und verfolge deinen Fortschritt über die Zeit.", unlock: "Premium freischalten", badge: "Premium", activeBadge: "Premium Aktiv", activeDescription: "Du hast Zugang zu allen Funktionen." },
  stats: {
    title: "Statistiken", last7Days: "Letzte 7 Tage (Minuten)", last4Weeks: "Letzte 4 Wochen (Minuten)",
    total: "Gesamt", sessions: "Sessions", avgPerSession: "Ø pro Session", streak: "Streak",
    days: "Tage", minutes: "Min", hours: "Std", noSessions: "Noch keine Sessions aufgezeichnet. Starte deine erste Übungssession!",
    currentWeek: "aktuell", weekPrefix: "KW",
    milestones: "Meilensteine", nextMilestone: "Nächster Meilenstein", dailyGoal: "Tägliches Ziel", goalReached: "Ziel erreicht!",
    midiPremiumTitle: "MIDI Performance",
    midiPremiumDesc: "Schalte detailliertes Hit-Tracking, Instrumentenverteilung und Performance-Trends mit einem MIDI-Controller frei.",
    midiPremiumCta: "Premium freischalten",
    midiConnectTitle: "MIDI Performance",
    midiConnectDesc: "Verbinde einen MIDI-Controller und starte eine Übungs-Session, um hier deine Performance-Statistiken zu sehen.",
    midiConnectCta: "Session starten",
  },
  nav: { stats: "Statistiken", drumPattern: "Drum Pattern", sessionMode: "Session Modus", faq: "FAQ" },
  notFound: { title: "404", message: "Seite nicht gefunden", backHome: "Zurück zur Startseite" },
  drumPattern: {
    title: "Drum Pattern", subtitle: "Erstelle & spiele Drum-Beats", play: "Abspielen", stop: "Stopp",
    bpm: "BPM", metronome: "Metronom", clear: "Löschen", clearConfirm: "Alle Beats löschen?",
    export: "Exportieren", import: "Importieren",
    kick: "Kick", snare: "Snare", hihat: "Hi-Hat", openhihat: "Open Hi-Hat", pedalhat: "Hi-Hat (Fuß)",
    hitom: "Hi Tom", midtom: "Mid Tom", lotom: "Lo Tom", crash: "Crash", ride: "Ride", splash: "Splash",
    steps16: "16 Steps", steps32: "32 Steps", patternSaved: "Pattern exportiert", patternLoaded: "Pattern importiert", patternTruncated: "Pattern auf erlaubte Anzahl Bars gekürzt",
    addTrack: "Spur hinzufügen", removeTrack: "Entfernen", noTracksMessage: "Füge eine Spur hinzu, um zu starten",
    measures: "Bars", patterns: "Patterns", myPatterns: "Meine Patterns", presets: "Presets",
    savePattern: "Speichern", namePlaceholder: "Pattern-Name...", noPatterns: "Noch keine Patterns gespeichert",
    deletePattern: "Löschen", patternDeleted: "Pattern gelöscht",
    presetRock: "Basic Rock", presetFunk: "Funk", presetShuffle: "Shuffle",
    presetBossaNova: "Bossa Nova", presetReggae: "Reggae", presetDnB: "Drum & Bass",
    maxPatternsReached: "Limit erreicht (3) – ", premiumPresetsLocked: "Premium erforderlich", upgradeToPremium: "Premium freischalten", premiumPresets: "Premium Presets",
    copyMeasure: "Bar duplizieren", measureLabel: "Bar", measureCopied: "Bar dupliziert", moveMeasure: "Ziehen zum Umordnen",
    midi: {
      connect: "MIDI verbinden", disconnect: "Trennen", device: "MIDI-Gerät", mapping: "MIDI-Mapping",
      learn: "Lernen", learning: "Pad schlagen...", record: "Aufnehmen", recording: "Aufnahme läuft", stopRecording: "Stopp",
      score: "Trefferquote", tolerance: "Toleranz", noDevice: "Kein MIDI-Gerät gefunden",
      notSupported: "MIDI wird in diesem Browser nicht unterstützt. Bitte Chrome oder Edge verwenden.",
      premiumRequired: "Premium erforderlich", patternRecorded: "Pattern aufgenommen",
      latency: "Latenz", latencyHint: "Kompensiert die Hardware-Verzögerung. Erhöhe den Wert, wenn Schläge zu spät erkannt werden.",
      tapTest: "Tap-Test", tapTestStop: "Stopp", tapTestApply: "Übernehmen",
      tapTestHint: "Schlage dein Pad 8-10 Mal gleichmäßig, um die Hardware-Latenz zu messen.",
      midiSound: "MIDI Sound", midiVolume: "MIDI Lautstärke",
      wizard: "Assistent",
      wizardTitle: "MIDI-Setup-Assistent", wizardStep: "Schritt",
      wizardHitPrompt: "Schlage jetzt dein:", wizardMultiZone: "Schlage alle Zonen dieses Pads/Beckens. Drücke Weiter wenn fertig.",
      wizardWaiting: "Warte auf MIDI-Eingabe...", wizardRestart: "Neustart",
      wizardSkip: "Überspringen", wizardNext: "Weiter", wizardFinish: "Fertig",
    },
    mute: "Stumm", unmute: "Laut", patternVolume: "Pattern", countIn: "Einzählen",
    metronomeSound: "Sound", classicSynth: "Classic/Synth", premiumLockedShort: "Premium",
    timelineView: "Timeline", notation: "Sheet",
    moreMeasures: "Mehr Bars", moreMeasuresHint: "Schalte bis zu 64 Bars frei mit Vollständige Features",
    measureLimitReached: "Bar-Limit erreicht",
  },
  auth: {
    loginTitle: "Anmelden", registerTitle: "Registrieren", forgotPasswordTitle: "Passwort zurücksetzen", newPasswordTitle: "Neues Passwort setzen",
    email: "E-Mail", password: "Passwort", firstName: "Vorname", lastName: "Nachname",
    loginButton: "Anmelden", registerButton: "Registrieren", sendResetLink: "Link senden", updatePassword: "Passwort aktualisieren",
    forgotPassword: "Passwort vergessen?", noAccount: "Kein Konto? Registrieren", hasAccount: "Schon ein Konto? Anmelden", rememberMe: "Eingeloggt bleiben",
    backToLogin: "Zurück zum Login", checkEmail: "Prüfe deine E-Mails", confirmEmail: "Wir haben dir einen Bestätigungslink gesendet.",
    loginError: "Anmeldung fehlgeschlagen", registerError: "Registrierung fehlgeschlagen", passwordTooShort: "Passwort muss mindestens 6 Zeichen haben",
    resetEmailSent: "Prüfe deine E-Mails für den Reset-Link.", passwordUpdated: "Passwort erfolgreich aktualisiert",
    invalidResetLink: "Ungültiger oder abgelaufener Reset-Link.", newPassword: "Neues Passwort",
    profile: "Profil", admin: "Admin", logout: "Abmelden", save: "Speichern", profileSaved: "Profil gespeichert", plan: "Plan", loginRequired: "Anmeldung erforderlich",
    verifying: "Wird überprüft...", invalidLink: "Ungültiger oder abgelaufener Link.", linkExpired: "Dieser Link ist abgelaufen oder ungültig. Bitte fordere einen neuen Einladungslink an.",
    dailyGoalLabel: "Tägliches Übungsziel (Min)",
    username: "Benutzername", emailOrUsername: "E-Mail oder Benutzername",
    usernameTaken: "Benutzername bereits vergeben", usernameInvalid: "3-30 Zeichen, beginnt mit Buchstabe, nur Buchstaben/Zahlen/_",
    notifications: "Benachrichtigungen", emailNotifications: "E-Mail-Benachrichtigungen",
    notifyStatusChange: "Statusänderungen", notifyNewComment: "Neue Kommentare",
    alreadyLoggedIn: "Du bist angemeldet als", goToDashboard: "Zum Dashboard", switchUser: "Als anderer Nutzer anmelden", adminPanel: "Admin-Panel",
    registrationClosed: "Registrierung geschlossen", registrationClosedMessage: "Die Registrierung ist derzeit geschlossen. Bitte versuche es später erneut.", registrationClosedWaitlist: "Zur Warteliste",
    earlyAccessBadge: "Early Access",
    earlyAccessTitle: "Sichere dir deinen Early-Access-Platz",
    earlyAccessDescription: "drumLog.Pro befindet sich aktuell in einer geschlossenen Beta. Trag dich auf die Warteliste ein und sei einer der Ersten, die mitgestalten.",
    earlyAccessBenefit1: "Kostenloser Zugang während der Beta-Phase",
    earlyAccessBenefit2: "Direkter Einfluss auf neue Features und Roadmap",
    earlyAccessBenefit3: "Vergünstigte Preise zum offiziellen Launch für Premium-Features",
    earlyAccessCta: "Jetzt auf die Warteliste",
    earlyAccessLoginHint: "Bereits registriert?",
    privacyConsent: "Mit der Registrierung stimmst du unserer",
    privacyPolicy: "Datenschutzerklärung",
    imprint: "Impressum",
    // Account deletion
    deleteAccount: "Konto löschen",
    deleteAccountDesc: "Beantrage die endgültige Löschung deines Kontos. Ein Administrator prüft deinen Antrag und entfernt anschließend alle deine Daten unwiderruflich aus unserem System (E-Mail, Passwort, Patterns, Übungsdaten).",
    requestDeleteAccount: "Konto-Löschung beantragen",
    deleteAccountConfirmTitle: "Konto-Löschung beantragen",
    deleteAccountConfirmDesc: "Diese Aktion sendet einen Antrag an die Administratoren. Nach Genehmigung werden alle deine Daten unwiderruflich gelöscht. Tippe zur Bestätigung das folgende Wort:",
    deleteAccountConfirmButton: "Antrag stellen",
    deleteAccountReason: "Grund (optional, hilft uns)",
    deleteAccountReasonPlaceholder: "z.B. Ich nutze die App nicht mehr...",
    deletionRequested: "Du hast die Löschung deines Kontos beantragt am",
    deletionPendingHint: "Ein Administrator prüft deinen Antrag. Bis dahin bleibt dein Konto nutzbar. Du kannst den Antrag jederzeit widerrufen.",
    withdrawDeletionRequest: "Antrag widerrufen",
    deletionRequestSuccess: "Antrag auf Konto-Löschung gestellt.",
    deletionWithdrawn: "Antrag widerrufen.",
    deletionRequestError: "Fehler beim Speichern des Antrags.",
    typeWord: "Sicherheitswort eingeben...",
    cancel: "Abbrechen",
    exportData: "Deine Daten exportieren",
    exportDataDesc: "Lade eine Kopie all deiner Daten als JSON herunter (Profil, Übungen, Sessions, Patterns). Erfüllt DSGVO Art. 15 & 20.",
    exportDataButton: "Daten herunterladen",
    exportDataLoading: "Wird vorbereitet...",
    exportDataSuccess: "Export erfolgreich heruntergeladen.",
    exportDataError: "Export fehlgeschlagen.",
    exportBeforeDeleteHint: "Möchtest du vorher deine Daten sichern? Nach der Löschung sind sie unwiderruflich weg.",
    exportNow: "Jetzt exportieren",
  },
  admin: {
    title: "Admin-Dashboard", search: "Suchen...", addUser: "Benutzer anlegen", editUser: "Benutzer bearbeiten", create: "Erstellen",
    name: "Name", emailCol: "E-Mail", role: "Rolle", planCol: "Plan", status: "Status", actions: "Aktionen",
    blocked: "Gesperrt", active: "Aktiv", unconfirmed: "Unbestätigt", noUsers: "Keine Benutzer gefunden",
    userDeleted: "Benutzer gelöscht", userUpdated: "Benutzer aktualisiert", userCreated: "Benutzer erstellt",
    passwordLength: "Passwort muss 8-72 Zeichen lang sein",
    maintenanceMode: "Wartungsmodus", maintenanceReason: "Wartungsgrund", maintenanceReasonPlaceholder: "z.B. Wir führen ein System-Update durch...",
    maintenanceActive: "Aktiv", maintenanceInactive: "Inaktiv", maintenanceSaved: "Wartungsmodus aktualisiert",
    settingsSaved: "Einstellungen gespeichert", appVersion: "App-Version & Status", versionLabel: "Version",
    maintenanceBypass: "Wartungs-Bypass",
    usersTab: "Benutzer", settingsTab: "Einstellungen", paymentsTab: "Zahlungen", lastLogin: "Letzter Login", online: "Online", offline: "Offline", never: "Nie",
    paymentsOverview: "Übersicht", subscriptionsTab: "Abonnements", paymentHistoryTab: "Zahlungen", paymentErrorsTab: "Fehler", activityLogTab: "Logbuch",
    monthlyRecurring: "Monatlich wiederkehrend", pastDueWarning: "überfällig",
    stripeUnreachable: "Nicht erreichbar", stripeConnected: "Verbunden", stripeError: "Fehler",
    // Account deletion requests (admin)
    pendingDeletions: "Löschungsanträge",
    pendingDeletionsBanner: "offene Löschungsanträge",
    showDeletionRequests: "Anträge anzeigen",
    showAllUsers: "Alle Nutzer zeigen",
    deleteAllRequested: "Alle endgültig löschen",
    deletionRequestedBadge: "Löschung beantragt",
    bulkDeleteTitle: "Mehrere Konten endgültig löschen",
    bulkDeleteDesc: "Du bist dabei, alle ausgewählten Konten und sämtliche zugehörigen Daten unwiderruflich zu löschen. Tippe zur Bestätigung das folgende Wort:",
    bulkDeleteConfirm: "Alle endgültig löschen",
    bulkDeleteProgress: "Lösche...",
    gracePending: "Grace-Period aktiv",
    graceReady: "Bereit",
    deletionRequestedOn: "Beantragt am",
    deletionReason: "Grund",
    waitingFor: "noch",
    daysShort: "T",
    of: "von",
    cancel: "Abbrechen",
    typeWord: "Sicherheitswort eingeben...",
    deletionRequestedAlert: "Konto-Löschung beantragt",
    deletionNoReason: "Kein Grund angegeben",
    gracePeriodRemaining: "Grace-Period: noch {n} von 7 Tagen",
    gracePeriodEnded: "Grace-Period beendet — bereit zur Löschung",
    // Insights dashboard
    insightsTab: "Insights",
    totalUsers: "Nutzer gesamt",
    paid: "Premium",
    free: "Free",
    paidShare: "Premium-Anteil",
    freeShare: "Free-Anteil",
    newUsersRange: "Neue Nutzer",
    waitlistTotal: "Warteliste gesamt",
    waitlistPending: "Warteliste offen",
    invitesSent: "Einladungen gesendet",
    invitesConverted: "Konvertiert",
    conversionRate: "Konversionsrate",
    invitesNotConverted: "Nicht konvertiert",
    staleInvites: "Veraltete Einladungen",
    customizeCards: "Karten anpassen",
    hideCard: "Karte ausblenden",
    showCard: "Karte einblenden",
    onlineNow: "Jetzt online",
    onlineNowDetail: "Aktiv in den letzten {minutes} Min.",
    onlinePeak: "Peak gleichzeitig online",
    onlinePeakDetail: "am {date}",
    onlinePeakNoRecord: "Noch kein Peak erfasst",
    onlineSinceTrackingStart: "seit Tracking-Start",
    onlineSinceReset: "seit Reset {date}",
    resetOnlinePeak: "Peak zurücksetzen",
    resetOnlinePeakTitle: "Online-Peak zurücksetzen?",
    resetOnlinePeakDescription: "Der neue Peak wird auf die aktuell online gezählten Benutzer gesetzt.",
    resetOnlinePeakConfirm: "Peak zurücksetzen",
    refreshOnlinePresence: "Online-Präsenz aktualisieren",
    onlinePeakResetSuccess: "Online-Peak zurückgesetzt",
    onlinePresenceLoadError: "Online-Präsenz konnte nicht geladen werden",
    catUsers: "Nutzer & Wachstum",
    catRevenue: "Abos & Umsatz",
    catWaitlist: "Waitlist & Funnel",
    catSystem: "System & Status",
    activeUsers: "Aktive Nutzer",
    earlyAccessUsers: "Early-Access-Nutzer",
    earlyAccessDetail: "Aus Waitlist konvertiert (ohne Test-User)",
    activeSubscriptions: "Aktive Abos",
    mrr: "MRR",
    revenue: "Umsatz",
    cancellations: "Kündigungen",
    newCustomers: "Neue Kunden",
    stripeStatus: "Stripe-Status",
    stripeHealthy: "Verbunden",
    stripeUnhealthy: "Fehler",
    stripeLive: "Live",
    stripeTest: "Test",
    subsMonthly: "Monatliche Abos",
    subsYearly: "Jährliche Abos",
    subsLifetime: "Lifetime-Mitglieder",
    subsMonthlyDetail: "Aktive monatliche Mitgliedschaften",
    subsYearlyDetail: "Aktive jährliche Mitgliedschaften",
    subsLifetimeDetail: "Einmalkauf, dauerhaft",
    // Sidebar navigation
    navUsers: "Nutzer",
    navCommunity: "Community",
    navBusiness: "Business",
    navSystem: "System",
    navUserList: "Übersicht",
    navInsights: "Insights",
    navWaitlist: "Waitlist",
    navFeedback: "Feedback",
    navChangelog: "Changelog",
    navProject: "Project Board",
    navFaq: "FAQ",
    navPayments: "Payments",
    navProfitability: "Profitabilität",
    navProducts: "Produkte & Preise",
    navSettings: "Einstellungen",
    navSamples: "Samples",
  },
  upgrade: {
    title: "Premium", heroTitle: "Entfalte dein volles Potenzial", heroSubtitle: "Hebe dein Drumming aufs nächste Level mit MIDI, Premium-Presets und mehr.",
    monthly: "Monatlich", yearly: "Jährlich", lifetime: "Lifetime", perMonth: "Monat", perYear: "Jahr", oneTime: "einmalig",
    freeDesc: "Starte mit den Grundlagen", premiumDesc: "Alles, was du zum Durchstarten brauchst",
    currentPlan: "Aktueller Plan", recommended: "EMPFOHLEN", subscribe: "Jetzt abonnieren",
    yearlySaving: "Spare 50% im Vergleich zu monatlich", lifetimeDesc: "Einmal zahlen, für immer nutzen",
    alreadyPremium: "Du bist Premium-Mitglied!",
    manageSubscription: "Abo verwalten", successTitle: "Willkommen bei Premium!",
    comparisonTitle: "Feature-Vergleich", feature: "Feature",
    featurePatterns: "Drum Patterns & Basis-Presets", featureMetronome: "Metronom & Übungs-Timer",
    featureStats: "Übungsstatistiken", featureMidi: "MIDI-Controller-Unterstützung",
    featurePremiumPresets: "Premium-Presets", featureUnlimited: "Unbegrenzte gespeicherte Patterns",
    featureRecording: "Session-Aufzeichnung",
    featureMeasures: "Drum Pattern Bars",
    featureRecordingTime: "Aufnahmezeit",
    featurePracticeTracker: "Übungs-Tracker & Tagesroutine",
    backToPlans: "Zurück zur Planauswahl",
    checkoutTitle: "Kauf abschließen",
    estimatedPriceNotice: "Geschätzter Preis. Der endgültige Betrag wird im Checkout anhand der aktuellen Wechselkurse festgelegt und kann abweichen.",
  },
  practiceTracker: {
    title: "Übungs-Tracker", subtitle: "Baue deine tägliche Routine",
    today: "Heute", songs: "Songs", rudiments: "Rudiments", warmups: "Aufwärmen", history: "Verlauf", library: "Bibliothek", session: "Session",
    addSong: "Song hinzufügen", addRudiment: "Rudiment hinzufügen", addWarmup: "Aufwärmübung hinzufügen", addItem: "Hinzufügen",
    songTitle: "Songtitel", songTitlePlaceholder: "z.B. Back in Black",
    artist: "Künstler", artistPlaceholder: "z.B. AC/DC",
    difficulty: "Schwierigkeit", selectDifficulty: "Schwierigkeit wählen",
    beginner: "Anfänger", intermediate: "Fortgeschritten", advanced: "Profi",
    personalNotes: "Notizen", notesPlaceholder: "Deine Notizen zu diesem Song...",
    rudimentName: "Rudiment", selectRudiment: "Rudiment wählen",
    exerciseName: "Übungsname", exerciseNamePlaceholder: "z.B. Single Stroke Roll auf dem Pad",
    exerciseDescription: "Beschreibung", descriptionPlaceholder: "Beschreibe die Übung (z.B. Stick Control, Pad-Arbeit)...",
    durationMin: "Dauer (Min)", repetitions: "Wiederholungen",
    interval: "Übungsintervall",
    intervals: { noSchedule: "Kein Intervall (nur manuell)", daily: "Täglich", every2: "Alle 2 Tage", every3: "Alle 3 Tage", every5: "Alle 5 Tage", weekly: "Wöchentlich" },
    save: "Speichern", cancel: "Abbrechen",
    itemAdded: "Eintrag hinzugefügt", itemUpdated: "Eintrag aktualisiert", itemDeleted: "Eintrag gelöscht",
    noSongs: "Noch keine Songs. Füge deinen ersten Song hinzu!", noRudiments: "Noch keine Rudiments. Füge dein erstes Rudiment hinzu!",
    noWarmups: "Noch keine Aufwärmübungen. Füge deine erste Übung hinzu!",
    noItemsToday: "Heute keine Einträge geplant", addItemsHint: "Füge Einträge in den Tabs Songs, Rudiments oder Aufwärmen hinzu",
    progress: "Heutiger Fortschritt", finishSession: "Session beenden", sessionFinished: "Session gespeichert!", startSession: "Session starten",
    launchMetronome: "Metronom starten", openDrumPattern: "Drum Pattern öffnen",
    streakDays: "Tage Streak", totalSessions: "Sessions", itemsCompleted: "Erledigt",
    last90Days: "Letzte 90 Tage", items: "Einträge", recentSessions: "Letzte Sessions",
    noSessions: "Noch keine Sessions",
    premiumRequired: "Practice Tracker ist ein Premium-Feature",
    unlockPractice: "Practice Tracker freischalten",
    startTimer: "Timer starten", stopTimer: "Timer stoppen", practicing: "Übend...",
    totalPracticeTime: "Gesamte Übungszeit", practiceByCategory: "Zeit nach Kategorie",
    warmupTime: "Aufwärmen", rudimentTime: "Rudiments", songTime: "Songs",
    originalBpm: "Original-BPM", practiceBpm: "Übungs-BPM", bpmProgress: "BPM-Fortschritt",
    skipToday: "Heute überspringen", skippedToday: "Heute übersprungen", undoSkip: "Rückgängig",
    noSessionRecorded: "Keine Session aufgezeichnet", noSessionPlanned: "Keine Session geplant", goToToday: "Heute",
    startFreeSession: "Freie Session starten", pickItemsForToday: "Items für heute auswählen",
    addToToday: "Für heute hinzufügen", removeFromToday: "Entfernen", adhocItems: "Zusätzliche Items",
    selectItemsTitle: "Items auswählen", selectItemsDesc: "Wähle Items aus deiner Bibliothek für die heutige Session",
    noItemsInLibrary: "Keine Items in der Bibliothek. Erstelle zuerst Songs, Rudiments oder Aufwärmübungen.",
    done: "Fertig",
    sessionSummaryTitle: "Session abgeschlossen!", sessionTotalDuration: "Gesamtdauer",
    sessionItemsCompleted: "Abgeschlossene Items", sessionGreatWork: "Gute Arbeit! Bleib dran.",
    sessionClose: "Schliessen",
    sessionTitleLabel: "Session-Name", sessionTitlePlaceholder: "Session benennen",
    sessionSaveTitle: "Speichern", sessionTitleSaved: "Session-Name gespeichert",
    sessionAutoTitleFallback: "Freie Session", sessionAutoTitleMoreOne: "1 weiteres",
    sessionAutoTitleMoreMany: "{count} weitere",
    sessionHistoryAll: "Session-Verlauf", sessionHistoryOpen: "Alle anzeigen",
    sessionHistoryLoadMore: "Mehr laden",
    sessionHistoryToday: "Heute", sessionHistoryYesterday: "Gestern",
    linkedPatterns: "Verknüpfte Patterns", linkPattern: "Pattern verknüpfen",
    unlinkPattern: "Verknüpfung entfernen", noPatterns: "Keine Patterns vorhanden",
    createPatternFirst: "Erstelle zuerst ein Drum Pattern im Pattern-Editor.",
    saveAsTemplate: "Als Vorlage speichern", loadTemplate: "Vorlage laden",
    templateName: "Vorlagenname", templateNamePlaceholder: "z.B. Montags-Routine",
    templateSaved: "Vorlage gespeichert", templateDeleted: "Vorlage gelöscht",
    noTemplates: "Noch keine Vorlagen", templateLoaded: "Vorlage geladen",
    deleteTemplate: "Vorlage löschen",
    goalProgress: "Zielfortschritt",
    goalType: "Zieltyp",
    goalNone: "Kein Ziel",
    goalTime: "Zeitziel",
    goalReps: "Wiederholungsziel",
    // YouTube & Video
    youtubeVideos: "YouTube Videos",
    youtubeUrlPlaceholder: "https://youtube.com/watch?v=...",
    videoLabelPlaceholder: "Label (optional)",
    addVideo: "Hinzufügen",
    videoAdded: "Video hinzugefügt",
    videoRemoved: "Video entfernt",
    videoLoop: "Wiederholen",
    videoSpeed: "Geschwindigkeit",
    // Inline Item Creator
    addNewItem: "Neues Item anlegen",
    addNewItemDesc: "Erstelle ein neues Item und füge es zur heutigen Session hinzu",
    createAndAdd: "Erstellen & Hinzufügen",
    // Focus Overlay
    focusMode: "Fokus-Modus",
    originalBpmReached: "Original-Tempo erreicht!",
    nextUp: "Weiter",
    // Library
    allItems: "Alle", searchPlaceholder: "Suchen...",
    newCategory: "Neue Kategorie", editCategory: "Kategorie bearbeiten",
    categoryNamePlaceholder: "z.B. Groove-Übungen", color: "Farbe", delete: "Löschen",
  },
  dataReset: {
    title: "Daten zurücksetzen",
    description: "Lösche deine Übungsdaten dauerhaft. Dies kann nicht rückgängig gemacht werden.",
    resetStats: "Statistiken zurücksetzen",
    resetStatsDesc: "Alle Übungs-Abschlüsse und Zeitdaten löschen.",
    resetSessions: "Sessions zurücksetzen",
    resetSessionsDesc: "Alle gespeicherten Übungs-Sessions löschen.",
    resetAll: "Alles zurücksetzen",
    resetAllDesc: "Alle Sessions, Abschlüsse und Übungseinträge löschen.",
    confirmTitle: "Bist du sicher?",
    confirmDescription: "Diese Aktion ist unwiderruflich. Tippe das folgende Wort zur Bestätigung ein:",
    typeWord: "Hier eingeben...",
    confirmWord: "Löschen",
    cancel: "Abbrechen",
    confirm: "Löschen bestätigen",
    resetSuccess: "Daten erfolgreich gelöscht.",
    resetError: "Fehler beim Löschen der Daten.",
  },
  imprint: {
    title: "Impressum",
    legalInfoTitle: "Angaben gemäß § 5 TMG",
    contactTitle: "Kontakt",
    disputeTitle: "Hinweis gemäß § 36 VSBG",
    disputeText: "René Zimmermann ist nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.",
    euDisputeTitle: "EU-Streitschlichtung",
    euDisputeText: "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:",
    privacyLink: "Datenschutzerklärung",
  },
  privacy: {
    title: "Datenschutzerklärung",
    responsibleTitle: "1. Verantwortlicher",
    responsibleText: "",
    principlesTitle: "2. Grundsätze der Datenverarbeitung",
    principlesText: "Wir verarbeiten personenbezogene Daten nur, soweit dies gesetzlich erlaubt ist oder Sie eingewilligt haben. Die Verarbeitung erfolgt auf Basis der DSGVO, insbesondere Art. 6 DSGVO.",
    hostingTitle: "3. Hosting & Infrastruktur",
    hostingText: "Diese Web-App wird über moderne Cloud-Infrastruktur bereitgestellt. Beim Aufruf der Website werden durch den Browser automatisch Informationen an den Server übermittelt (Server-Logfiles), u.\u00a0a. IP-Adresse, Datum/Uhrzeit, aufgerufene URL, Browser und Betriebssystem. Diese Daten werden zur technischen Bereitstellung des Dienstes benötigt (Art. 6 Abs. 1 lit. f DSGVO) und nach spätestens 7 Tagen gelöscht.",
    accountTitle: "4. Nutzerkonto & Datenspeicherung (Supabase)",
    accountIntro: "Für die Nutzung von drumLog.pro ist eine Registrierung möglich. Dabei werden folgende Daten gespeichert:",
    accountEmail: "E-Mail-Adresse",
    accountPassword: "Passwort (verschlüsselt / gehasht, nicht im Klartext)",
    accountPracticeData: "Übungsdaten (Sessions, Patterns, Fortschritte)",
    accountSupabaseText: "Diese Daten werden bei Supabase Inc. gespeichert. Der genutzte Datenbankserver befindet sich in der EU (Frankfurt, AWS eu-central-1). Supabase ist als Auftragsverarbeiter gemäß Art. 28 DSGVO vertraglich gebunden. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung).",
    accountSupabaseInfo: "Supabase Inc., 970 Toa Payoh North, Singapur",
    cookiesTitle: "6. Cookies & lokale Speicherung",
    cookiesText: "Diese Website verwendet Cookies und localStorage. Beim ersten Besuch wird ein Cookie-Consent-Banner eingeblendet. Nicht notwendige Cookies (Analyse, Funktional, Marketing) werden nur nach Ihrer ausdrücklichen Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO i.\u00a0V.\u00a0m. § 25 TTDSG gesetzt.",
    cookiesNecessary: "Notwendige Cookies (keine Einwilligung erforderlich, § 25 Abs. 2 TTDSG):",
    cookiesFunctional: "Funktionale Cookies (nur mit Einwilligung, Art. 6 Abs. 1 lit. a DSGVO i.\u00a0V.\u00a0m. § 25 Abs. 1 TTDSG):",
    cookiesFunctionalWaitlist: "Speichert nach Eintragung in die Early-Access-Warteliste eine Wiedererkennung (E-Mail-Hinweis), damit Ihnen das Anmeldeformular nicht erneut angezeigt wird und Sie den Status Ihrer Bestätigung sehen. Speicherdauer: 1 Jahr. Wird beim Widerruf Ihrer Einwilligung automatisch gelöscht.",
    analyticsTitle: "7. Google Analytics",
    analyticsIntro: "Mit Ihrer Einwilligung verwenden wir Google Analytics 4, einen Webanalysedienst der Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland.",
    analyticsCookies: "Google Analytics setzt Cookies, die eine Analyse der Nutzung der Website ermöglichen. Die dadurch erzeugten Informationen werden in der Regel an einen Server von Google in den USA übertragen. Wir haben die IP-Anonymisierung aktiviert.",
    analyticsPrivacyFramework: "Für die Übermittlung in die USA hat Google sich dem EU-US Data Privacy Framework unterworfen (Angemessenheitsbeschluss der EU-Kommission vom 10. Juli 2023).",
    analyticsRevoke: "Sie können Ihre Einwilligung jederzeit über den Cookie-Einstellungen-Button (unten links auf der Seite) widerrufen.",
    analyticsLegal: "Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO i.\u00a0V.\u00a0m. § 25 Abs. 1 TTDSG.",
    rightsTitle: "8. Ihre Rechte (Art. 15–22 DSGVO)",
    rightsText: "Sie haben das Recht auf Auskunft über gespeicherte Daten (Art. 15 DSGVO), Berichtigung unrichtiger Daten (Art. 16 DSGVO), Löschung Ihrer Daten (Art. 17 DSGVO), Einschränkung der Verarbeitung (Art. 18 DSGVO), Datenübertragbarkeit (Art. 20 DSGVO), Widerspruch gegen die Verarbeitung (Art. 21 DSGVO) sowie Widerruf einer erteilten Einwilligung mit Wirkung für die Zukunft (Art. 7 Abs. 3 DSGVO).",
    rightsContact: "Zur Ausübung Ihrer Rechte wenden Sie sich an:",
    rightsAuthority: "Zuständige Aufsichtsbehörde:",
    securityTitle: "9. Datensicherheit",
    securityText: "Wir setzen technische und organisatorische Sicherheitsmaßnahmen ein, um Ihre Daten gegen Manipulation, Verlust oder unberechtigten Zugriff zu schützen. Die Übertragung erfolgt ausschließlich verschlüsselt via HTTPS/TLS.",
    currentTitle: "10. Aktualität dieser Datenschutzerklärung",
    currentText: "Diese Datenschutzerklärung hat den Stand: Februar 2025. Bei wesentlichen Änderungen werden registrierte Nutzer informiert.",
    imprintLink: "Impressum",
  },
  cookieConsent: {
    title: "Wir respektieren deine Privatsphäre",
    description: "drumLog.pro verwendet Cookies, um dir die bestmögliche Nutzererfahrung zu bieten. Notwendige Cookies sind für den Betrieb der App erforderlich. Mit deiner Zustimmung verwenden wir weitere Cookies für Analyse- und Funktionszwecke. Du kannst deine Einwilligung jederzeit widerrufen.",
    acceptAll: "Alle akzeptieren",
    rejectAll: "Alle ablehnen",
    managePreferences: "Einstellungen anpassen",
    settingsDescription: "Wähle aus, welche Cookies du zulassen möchtest. Notwendige Cookies können nicht deaktiviert werden, da sie für die Grundfunktionen der App erforderlich sind.",
    confirmSelection: "Auswahl bestätigen",
    necessaryLabel: "Notwendig – immer aktiv",
    necessaryDescription: "Session-Management, Sicherheit und grundlegende App-Funktionen.",
    functionalLabel: "Funktional",
    functionalDescription: "Speicherung von Übungseinstellungen, Metronom-Konfiguration und UI-Präferenzen.",
    analyticsLabel: "Analyse",
    analyticsDescription: "Anonyme Nutzungsstatistiken zur Verbesserung der App.",
    marketingLabel: "Marketing",
    marketingDescription: "Einbettungen von Drittanbietern und Social-Sharing-Funktionen.",
    privacyPolicy: "Datenschutzerklärung",
    imprint: "Impressum",
    settingsAriaLabel: "Cookie-Einstellungen öffnen",
  },
  feedback: {
    submitTitle: "Feedback einreichen", typeBug: "Bug-Report", typeFeature: "Feature-Wunsch", typeFeedback: "Support",
    titleLabel: "Titel", titlePlaceholder: "Kurze Zusammenfassung...", descriptionLabel: "Beschreibung", descriptionPlaceholder: "Beschreibe es im Detail...",
    stepsLabel: "Schritte zum Reproduzieren", stepsPlaceholder: "1. Gehe zu...\n2. Klicke auf...", expectedLabel: "Erwartetes Verhalten", expectedPlaceholder: "Was sollte passieren?",
    actualLabel: "Tatsächliches Verhalten", actualPlaceholder: "Was passiert tatsächlich?", screenshots: "Screenshots", addScreenshot: "Hinzufügen",
    submit: "Absenden", submitted: "Feedback eingereicht!", supportSubmitted: "Supportanfrage erfolgreich gesendet!",
    subscribeEmail: "Per E-Mail benachrichtigen", subscribedToast: "Abonniert — du erhältst E-Mail-Benachrichtigungen",
    unsubscribedToast: "Abbestellt", subscribeButton: "Abonnieren", unsubscribeButton: "Abonniert",
    globalEmailEnabled: "E-Mail-Benachrichtigungen wurden global aktiviert",
    boardTitle: "Community Hub", featureRequests: "Feature-Wünsche", bugTracker: "Bug-Tracker", whatsNew: "Neuigkeiten",
    roadmapTitle: "Roadmap", roadmap_planned: "In Planung", roadmap_in_progress: "In Arbeit", roadmap_completed: "Abgeschlossen", roadmapEmpty: "Noch nichts hier.",
    noItems: "Noch keine Einträge.", loginToVote: "Zum Abstimmen anmelden", noChangelog: "Noch keine Updates.",
    submitIdea: "Idee einreichen", reportBug: "Bug melden",
    supportTab: "Support", submitSupport: "Anfrage senden", supportTitle: "Support kontaktieren",
    supportSubjectLabel: "Betreff", supportSubjectPlaceholder: "Wobei können wir helfen?",
    supportMessageLabel: "Nachricht", supportMessagePlaceholder: "Beschreibe dein Anliegen oder deine Frage...",
    contactEmailLabel: "Deine E-Mail", contactEmailPlaceholder: "deine@email.de",
    contactEmailHint: "Damit wir auf deine Anfrage antworten können.", contactEmailRequired: "Bitte gib eine Kontakt-E-Mail an.",
    supportLoginHint: "Melde dich an, um deine Support-Anfragen zu sehen, oder sende eine neue.",
    supportLogin: "Anmelden", noSupportItems: "Noch keine Support-Anfragen.",
    loggedInAsLabel: "Angemeldet als", loggedInHint: "Wir antworten an deine Account-E-Mail.", supportLoginInstead: "Oder anmelden",
  },
  subscription: {
    title: "Abonnement",
    monthly: "Monatlich", yearly: "Jährlich", lifetime: "Lifetime",
    manage: "Abo verwalten", upgrade: "Auf Premium upgraden",
    renewsOn: "Verlängert sich am", expiresOn: "Läuft ab am",
    lifetimeAccess: "Lifetime-Zugang — kein Ablaufdatum",
    autoRenewalOn: "Auto-Verlängerung aktiv", autoRenewalOff: "Auto-Verlängerung deaktiviert",
    pastDue: "Zahlung überfällig — bitte aktualisiere deine Zahlungsmethode",
    paymentHistory: "Zahlungshistorie",
  },
  waitlist: {
    title: "Sichere dir deinen Early-Access",
    subtitle: "Die Plätze sind begrenzt. Trag dich jetzt ein und sei vor allen anderen dabei, wenn drumLog.Pro startet — mit vollem Zugriff bevor wir öffentlich live gehen.",
    badgeLimited: "Limited Early Access",
    badgeFree: "Kostenlos",
    benefitNoSpam: "Kein Spam",
    benefitUnsubscribe: "Unverbindlich",
    benefitFree: "Kostenlos",
    benefitPriority: "Vorrang vor Public Launch",
    compactSubtitle: "Sei einer der Ersten, die drumLog.Pro nutzen.",
    heroTitle: "Sei vor allen anderen dabei",
    heroSubtitle: "Begrenzte Plätze — sichere dir jetzt deinen Zugang und überspringe die Public-Launch-Warteschlange.",
    emailPlaceholder: "Deine E-Mail-Adresse",
    gdprConsent: "Ich stimme zu, dass meine E-Mail-Adresse für meinen Early-Access-Platz auf der drumLog.Pro-Warteliste gespeichert wird.",
    gdprLink: "Datenschutzerklärung",
    submit: "Platz sichern",
    success: "Du bist auf der Liste! Wir benachrichtigen dich, wenn es losgeht.",
    alreadyRegistered: "Diese E-Mail ist bereits auf der Warteliste.",
    accountExists: "Ein Konto mit dieser E-Mail existiert bereits.",
    disabled: "Die Warteliste ist derzeit geschlossen.",
    invalidEmail: "Bitte gib eine gültige E-Mail-Adresse ein.",
    completeProfileTitle: "Profil vervollständigen",
    completeProfileSubtitle: "Richte dein Konto ein, um mit drumLog.Pro zu starten.",
    setPassword: "Passwort festlegen",
    profileCompleted: "Profil vervollständigt! Willkommen bei drumLog.Pro.",
    incompleteProfileBanner: "Deine Registrierung ist noch nicht abgeschlossen. Bitte vervollständige dein Profil.",
    incompleteProfileCta: "Profil vervollständigen",
    adminTab: "Warteliste",
    adminInvite: "Einladen",
    adminInviteAll: "Alle einladen",
    adminDelete: "Löschen",
    adminNoEntries: "Noch keine Wartelisten-Einträge.",
    adminInvited: "Eingeladen",
    adminInviteSuccess: "Einladung erfolgreich gesendet.",
    adminInviteAllSuccess: "Alle Einladungen gesendet.",
    adminDeleteSuccess: "Eintrag gelöscht.",
    statusUnconfirmed: "Unbestätigt",
    statusPending: "Wartend",
    statusInvited: "Eingeladen",
    statusConverted: "Konvertiert",
    statusRemoved: "Entfernt",
    waitlistEnabled: "Warteliste aktiv",
    waitlistToggle: "Wartelisten-Anmeldung auf Wartungsseite anzeigen",
    successCheckEmail: "Fast geschafft! Bestätige deine E-Mail-Adresse, um deinen Early-Access-Platz zu sichern.",
    confirmPageTitle: "E-Mail-Bestätigung",
    confirmPageSuccess: "Deine E-Mail wurde bestätigt! Du stehst jetzt auf der Warteliste.",
    confirmPageError: "Dieser Bestätigungslink ist ungültig oder wurde bereits verwendet.",
    confirmPageExpired: "Dieser Bestätigungslink ist abgelaufen. Bitte melde dich erneut an.",
    confirmPageLoading: "Deine E-Mail wird bestätigt...",
    rateLimited: "Eine Bestätigungs-E-Mail wurde bereits gesendet. Bitte prüfe dein Postfach.",
    resendInviteTitle: "Einladungslink abgelaufen?",
    resendInviteSubtitle: "Gib deine E-Mail-Adresse ein, um einen neuen Einladungslink zu erhalten.",
    resendInviteButton: "Neuen Einladungslink senden",
    resendInviteSuccess: "Falls diese E-Mail eingeladen wurde, haben wir einen neuen Link gesendet. Bitte prüfe dein Postfach.",
    alreadyOnListBadge: "Du bist dabei",
    alreadyOnListTitle: "Dein Early-Access-Platz ist reserviert",
    alreadyOnListSubtitle: "Deine E-Mail liegt uns vor. Sobald ein Platz frei wird, bist du als Erstes dran — du musst dich nicht erneut eintragen.",
    alreadyOnListHint: "Falsche E-Mail oder versehentlich eingetragen?",
    alreadyOnListReset: "Andere E-Mail verwenden",
    pendingConfirmationBadge: "Bestätigung ausstehend",
    pendingConfirmationTitle: "Bitte bestätige deine E-Mail",
    pendingConfirmationSubtitle: "Wir haben dir einen Bestätigungslink geschickt. Klicke auf den Link in der E-Mail, um deinen Platz auf der Early-Access-Liste zu sichern.",
    pendingConfirmationCheckSpam: "Schau auch in deinem Spam-Ordner nach.",
    pendingConfirmationResend: "Bestätigungsmail erneut senden",
    pendingConfirmationResending: "Wird gesendet...",
    pendingConfirmationResent: "Bestätigungsmail wurde erneut gesendet. Bitte prüfe dein Postfach.",
    pendingConfirmationResendError: "Senden fehlgeschlagen. Bitte versuche es später erneut.",
    resendInviteRateLimited: "Ein neuer Einladungslink wurde kürzlich gesendet. Bitte warte einige Minuten und prüfe dein Postfach.",
    resendInviteLink: "Einladungslink abgelaufen?",
    adminFilterAll: "Alle",
    adminFilterStatus: "Nach Status filtern",
    adminResendConfirmation: "Erneut senden",
    adminReinvite: "Erneut einladen",
    adminBulkReinvite: "Alle abgelaufenen erneut einladen",
    adminStaleInvites: "abgelaufene Einladung(en) — 14+ Tage ohne Kontoerstellung",
    adminInviteClicked: "Link geklickt",
    adminCountry: "Land",
    adminSelected: "ausgewählt",
    adminResendConfirmationSuccess: "Bestätigungs-E-Mail erneut gesendet.",
    adminReinviteSuccess: "Erneute Einladung gesendet.",
    adminBulkReinviteSuccess: "Abgelaufene Einträge erneut eingeladen.",
    adminRemindIncomplete: "Erinnern",
    adminRemindIncompleteSuccess: "Erinnerung gesendet.",
  },
};

export default de;
